import { useState } from "react";
import { useEffect } from "react";
import { fetch, trans } from "../../lib/utils";
import { useSelector } from "react-redux";
import store from "../../stores/store";
import {
  getLayerById,
  getMapCenter,
  getMapCenterProjected,
  getSize,
} from "../map/mapUtils";
import { Projection } from "ol/proj";
import { METERS_PER_UNIT } from "ol/proj/Units";
import Feature from "ol/Feature";
import { fromExtent } from "ol/geom/Polygon";
import { transformExtent } from "ol/proj";
import { GeoJSON, WKT } from "ol/format";
import { FeatureToGeoJSON } from "../../lib/featureUtils";
import { intersect } from "@turf/turf";

function PrintPanel() {
  const [printConfig, setPrintConfig] = useState(null);
  const [printSelectedConfig, setSelectedPrintConfig] = useState({});

  const [selectedBasemap, setSelectedBasemap] = useState(null);

  const [googleApiKey, setGoogleApiKey] = useState();

  const layers = useSelector((state) => store.getState().layers);
  const sources = useSelector((state) => store.getState().sources);

  const leftPanelActiveTab = useSelector(
    (state) => state.app.leftPanelActiveTab
  );

  useEffect(() => {
    if (!selectedBasemap && window["appConfig"]) {
      setGoogleApiKey(window["appConfig"]()["googleApiKey"]);
      setSelectedBasemap(window["appConfig"]()["print"]()["basemap"]);
    }
    fetch(
      window["appConfig"]()["api"](
        "?proxy=raw&contenttype=".concat(
          "application/json",
          "&url=",
          window["appConfig"]()["print"]()["infoUrl"]
        )
      ),
      "GET",
      {},
      null,
      (resp) => {
        if (!printConfig) {
          setPrintConfig(resp.data);
        }
      }
    );

    if (leftPanelActiveTab === "print") {
      window["printCalculateBounds"]();
      window["map"].un("moveend", window["printCalculateBounds"]);
      window["map"].on("moveend", window["printCalculateBounds"]);
    }
  }, [leftPanelActiveTab, printSelectedConfig]);

  window["printCalculateBounds"] = () => {
    if (printSelectedConfig.scale) {
      window["printLayer"].getSource().clear();
      let center = getMapCenter();
      let size = getSize();

      printConfig.layouts.forEach((el) => {
        if (el.name === printSelectedConfig.template) {
          size = [el.map.width, el.map.height];
        }
      });

      const units = window["map"].getView().getProjection().getUnits();
      const INCHES_PER_UNIT = {
        m: 39.37,
        degrees: 4374754,
      };

      var resolution =
        1 / ((1 / printSelectedConfig.scale) * INCHES_PER_UNIT[units] * 112);

      var halfWDeg = size[0] * resolution;
      var halfHDeg = size[1] * resolution;

      const boxFeature = new Feature({
        geometry: fromExtent([
          center[0] - halfWDeg,
          center[1] - halfHDeg,
          center[0] + halfWDeg,
          center[1] + halfHDeg,
        ]),
      });

      window["printLayer"].getSource().addFeature(boxFeature);
    }
  };

  const updateSelected = (property, value) => {
    let _printSelectedConfig = { ...printSelectedConfig };

    _printSelectedConfig[property] = value;

    setSelectedPrintConfig(_printSelectedConfig);
  };

  const print = () => {
    let printLayers = [];

    switch (selectedBasemap) {
      case "openstreetmap":
        printLayers.push({
          baseURL: "http://a.tile.openstreetmap.org",
          singleTile: false,
          type: "OSM",
          maxExtent: [
            -20037508.3392, -20037508.3392, 20037508.3392, 20037508.3392,
          ],
          tileSize: [256, 256],
          extension: "png",
          resolutions: [
            156543.0339, 78271.51695, 39135.758475, 19567.8792375,
            9783.93961875, 4891.969809375, 2445.9849046875, 1222.99245234375,
            611.496226171875, 305.7481130859375, 152.87405654296876,
            76.43702827148438, 38.21851413574219, 19.109257067871095,
            9.554628533935547, 4.777314266967774, 2.388657133483887,
            1.1943285667419434, 0.5971642833709717,
          ],
        });
        break;
      case "googlesatellite":
        printLayers.push({
          baseURL:
            "http://maps.google.com/maps/api/staticmap?key=" + googleApiKey,
          type: "TiledGoogle",
          sensor: false,
          maptype: "satellite",
          maxExtent: [
            -20037508.3392, -20037508.3392, 20037508.3392, 20037508.3392,
          ],
          extension: "png",
          format: "image/png",
          resolutions: [
            156543.0339, 78271.51695, 39135.758475, 19567.8792375,
            9783.93961875, 4891.969809375, 2445.9849046875, 1222.99245234375,
            611.496226171875, 305.7481130859375, 152.87405654296876,
            76.43702827148438, 38.21851413574219, 19.109257067871095,
            9.554628533935547, 4.777314266967774, 2.388657133483887,
            1.1943285667419434, 0.5971642833709717,
          ],
        });
        break;
      case "googleroadmap":
        printLayers.push({
          baseURL:
            "http://maps.google.com/maps/api/staticmap?key=" + googleApiKey,
          type: "TiledGoogle",
          sensor: false,
          maptype: "roadmap",
          maxExtent: [
            -20037508.3392, -20037508.3392, 20037508.3392, 20037508.3392,
          ],
          extension: "png",
          format: "image/png",
          resolutions: [
            156543.0339, 78271.51695, 39135.758475, 19567.8792375,
            9783.93961875, 4891.969809375, 2445.9849046875, 1222.99245234375,
            611.496226171875, 305.7481130859375, 152.87405654296876,
            76.43702827148438, 38.21851413574219, 19.109257067871095,
            9.554628533935547, 4.777314266967774, 2.388657133483887,
            1.1943285667419434, 0.5971642833709717,
          ],
        });
        break;
      case "googlehybrid":
        printLayers.push({
          baseURL:
            "http://maps.google.com/maps/api/staticmap?key=" + googleApiKey,
          type: "TiledGoogle",
          sensor: false,
          maptype: "hybrid",
          maxExtent: [
            -20037508.3392, -20037508.3392, 20037508.3392, 20037508.3392,
          ],
          extension: "png",
          format: "image/png",
          resolutions: [
            156543.0339, 78271.51695, 39135.758475, 19567.8792375,
            9783.93961875, 4891.969809375, 2445.9849046875, 1222.99245234375,
            611.496226171875, 305.7481130859375, 152.87405654296876,
            76.43702827148438, 38.21851413574219, 19.109257067871095,
            9.554628533935547, 4.777314266967774, 2.388657133483887,
            1.1943285667419434, 0.5971642833709717,
          ],
        });
        break;
      case "googleterrain":
        printLayers.push({
          baseURL:
            "http://maps.google.com/maps/api/staticmap?key=" + googleApiKey,
          type: "TiledGoogle",
          sensor: false,
          maptype: "terrain",
          maxExtent: [
            -20037508.3392, -20037508.3392, 20037508.3392, 20037508.3392,
          ],
          extension: "png",
          format: "image/png",
          resolutions: [
            156543.0339, 78271.51695, 39135.758475, 19567.8792375,
            9783.93961875, 4891.969809375, 2445.9849046875, 1222.99245234375,
            611.496226171875, 305.7481130859375, 152.87405654296876,
            76.43702827148438, 38.21851413574219, 19.109257067871095,
            9.554628533935547, 4.777314266967774, 2.388657133483887,
            1.1943285667419434, 0.5971642833709717,
          ],
        });
        break;
    }

    window["map"]?.getLayers().forEach((el) => {
      if (el.getVisible()) {
        if (el.get("layerId")) {
          let layer = layers[el.get("layerId")];

          switch (layer.source.type) {
            case "WMS":
              printLayers.push({
                baseURL: layer.source.url,
                singleTile: true,
                layers: [layer.name],
                opacity: layer.opacity,
                type: layer.source.type,
                format: "image/png",
                styles: [""],
              });
              break;

            default:
              let printFeatures = {
                type: "FeatureCollection",
                features: [],
              };

              el.getSource()
                .getFeatures()
                .forEach((feature) => {
                  try {
                    if (
                      intersect(
                        FeatureToGeoJSON(feature),
                        FeatureToGeoJSON(
                          window["printLayer"].getSource().getFeatures()[0]
                        )
                      )
                    ) {
                      feature.set("_style", {
                        fontColor: el
                          .getStyle()
                          .getText()
                          ?.getFill()
                          .getColor(),
                        fontSize: el.getStyle().getText()?.getScale(),
                        strokeColor: el.getStyle().getStroke()?.getColor(),
                        fillColor: el.getStyle().getFill().getColor(),
                        fill: el.getStyle().getFill().getColor(),
                        fontFamily: el.getStyle().getText()?.getFont(),
                        fillOpacity: 0,
                        pointRadius: 1,
                        strokeWidth: el.getStyle().getStroke().getWidth(),
                        strokeDashstyle: "solid",
                      });

                      feature.getGeometry().transform("EPSG:4326", "EPSG:3857");
                      printFeatures.features.push(FeatureToGeoJSON(feature));
                    }
                  } catch (e) {}
                });

              printLayers.push({
                type: "Vector",
                opacity: layer.opacity,
                styles: {
                  1: {
                    fontColor: el.getStyle().getText()?.getFill().getColor(),
                    fontSize: el.getStyle().getText()?.getScale(),
                    strokeColor: el.getStyle().getStroke()?.getColor(),
                    fillColor: el.getStyle().getFill().getColor(),
                    fill: el.getStyle().getFill().getColor(),
                    fontFamily: el.getStyle().getText()?.getFont(),
                    fillOpacity: 0,
                    pointRadius: 1,
                    strokeWidth: el.getStyle().getStroke().getWidth(),
                    strokeDashstyle: "dashdot",
                  },
                },
                name: layer.name,
                geoJson: printFeatures,
              });

              break;
          }
        }
      }
    });

    let postData = {
      units: "meters",
      srs: "EPSG:3857",
      geodetic: true,
      layout: printSelectedConfig.template,
      dpi: printSelectedConfig.dpi,
      mapTitle: printSelectedConfig.title,
      comment: printSelectedConfig.comment,
      resourcesUrl: "",
      termsOfUse: "Οροι Χρήσης: ...",
      layers: printLayers,
      pages: [
        {
          center: getMapCenterProjected("EPSG:3857"),
          geodetic: true,
          strictEpsg4326: true,
          scale: printSelectedConfig.scale,
          rotation: 0,
          results: {},
        },
      ],
      legends: [],
    };

    fetch(
      window["appConfig"]()["api"](
        "?proxy=getPrint".concat("&url=", printConfig.createURL)
      ),
      "POST",
      {},
      postData,
      (resp) => {
        window.open(
          resp.data.getURL.replace(
            "http://192.168.1.4:8081",
            "https://demo.getmap.gr"
          )
        );
      }
    );
  };

  return (
    <div className="mt-2 ms-2 me-2">
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">{trans("Title")}</label>
        <div className="input-group">
          <input
            className="form-control form-control-sm mt-2"
            value={printSelectedConfig.title}
            onChange={(e) => {
              updateSelected("title", e.target.value);
            }}
          />
        </div>
      </div>
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">{trans("Comment")}</label>
        <div className="input-group">
          <textarea
            className="form-control form-control-sm mt-2"
            value={printSelectedConfig.comment}
            onChange={(e) => {
              updateSelected("comment", e.target.value);
            }}
          />
        </div>
      </div>
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">
          {trans("Select template")}
        </label>
        <div className="input-group">
          <select
            className="form-select form-select-sm mt-2"
            value={printSelectedConfig.template}
            onChange={(e) => {
              updateSelected("template", e.target.value);
            }}
          >
            <option value="">- {trans("Select template")} -</option>
            {printConfig?.layouts?.map((opt, idx) => {
              return <option value={opt.name}>{opt.name}</option>;
            })}
          </select>
        </div>
      </div>
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">
          {trans("Select Basemap")}
        </label>
        <div className="input-group">
          <select
            className="form-select form-select-sm mt-2"
            value={selectedBasemap}
            onChange={(e) => {
              setSelectedBasemap(e.target.value);
            }}
          >
            <option value="openstreetmap">OpenStreetMap</option>
            <option value="googlesatellite">Google Satellite</option>
            <option value="googleroadmap">Google Roadmap</option>
            <option value="googlehybrid">Google Hybrid</option>
            <option value="googleterrain">Google Terrain</option>
          </select>
        </div>
      </div>
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">{trans("Select scale")}</label>
        <div className="input-group">
          <select
            className="form-select form-select-sm mt-2"
            value={printSelectedConfig.scale}
            onChange={(e) => {
              updateSelected("scale", e.target.value);
            }}
          >
            <option value="">- {trans("Select scale")} -</option>
            {printConfig?.scales?.map((opt, idx) => {
              return <option value={opt.value}>{opt.name}</option>;
            })}
          </select>
        </div>
      </div>
      <div className="mb-3 mt-2">
        <label className="color-get-blue small">{trans("Select DPI")}</label>
        <div className="input-group">
          <select
            className="form-select form-select-sm mt-2"
            value={printSelectedConfig.dpi}
            onChange={(e) => {
              updateSelected("dpi", e.target.value);
            }}
          >
            <option value="">- S{trans("Select DPI")} -</option>
            {printConfig?.dpis?.map((opt, idx) => {
              return <option value={opt.value}>{opt.name}</option>;
            })}
          </select>
        </div>
      </div>
      <div className="mb-3 mt-2 text-end">
        <button
          type="button"
          className="btn btn-sm btn-secondary text-white"
          onClick={print}
        >
          <i className="fas fa-print me-2"></i>
          {trans("Print")}
        </button>
      </div>
    </div>
  );
}

export default PrintPanel;
